import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  SpinnerService,
  ToastrNotificationService,
} from '@lbmx/phoenix-lib-utils';
import { BaseHttpService, Request } from '@lbmx/root-services';
import { TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { merge, of, Subject, Subscription } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ConfigProvider } from '../../provider/config-provider';
import { LoginAsConfig } from '../../services/loginAs/loginAs.service';
import { SecureService } from './secure.service';
import { apiUrls } from './url-config';

@Component({
  selector: 'app-login-as-modal',
  template: `<p-dialog
    [(visible)]="showModal"
    [showHeader]="false"
    [modal]="true"
    position="center"
    [draggable]="false"
    [resizable]="false"
    [responsive]="true"
    [closeOnEscape]="false"
    [ngClass]="{ 'secure-modal-dialog-loading': loading }"
    styleClass="marketplace-selector-modal"
    data-testid="marketplace-selector-modal"
  >
    <div class="vertical-flow marketplace-selector">
      <h2 class="center">{{ 'LOGIN_AS_MODAL.HEADER' | transloco }}</h2>
      <div class="center space-before-150 width-90">
        <div class="left">
          {{ 'LOGIN_AS_MODAL.BODY_A' | transloco }}
          {{ config?.username }}. {{ 'LOGIN_AS_MODAL.BODY_B' | transloco }}
          <br /><br />
          {{ 'LOGIN_AS_MODAL.BODY_C' | transloco }}
        </div>
      </div>
      <div
        class="space-before-250 form-controls space-between-075 horizontal-flow"
      >
        <button pButton (click)="cancel$.next()" class="ds secondary">
          {{ 'ACTIONS.CANCEL' | transloco }}
        </button>
        <button pButton (click)="submit$.next()" class="ds primary">
          {{ 'ACTIONS.ACKNOWLEDGE_AND_CONTINUE' | transloco }}
        </button>
      </div>
    </div>
  </p-dialog>`,
})
export class LoginAsModalComponent implements OnInit, OnDestroy {
  public showModal: boolean;
  public subscriptions: Subscription[] = [];

  public cancel$ = new Subject<void>();
  public submit$ = new Subject<void>();

  public loading = false;

  public config: LoginAsConfig;

  private _close$ = new Subject<void>();
  private _baseUrl = apiUrls.groupLoginAs(
    this.configPrv.AppSetting.uriBase.apiBaseUrl
  );

  @Input('showLoginAsModal') set showLoginAsModalSetter(state: LoginAsConfig) {
    this.showModal = !!state;
    if (this.showModal) {
      this.config = state;
      this.secureService.setSelectedMarketplace(false);
    }
  }

  constructor(
    private configPrv: ConfigProvider,
    private router: Router,
    private store: Store,
    private translocoService: TranslocoService,
    private secureService: SecureService,
    private http: BaseHttpService,
    private toast: ToastrNotificationService,
    public spinner: SpinnerService
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this.cancel$.pipe(tap(() => this._close$.next())).subscribe()
    );

    this.subscriptions.push(
      merge(this._close$)
        .pipe(
          tap(() => {
            this.showModal = false;
            this.secureService.setSelectedMarketplace(true);
          })
        )
        .subscribe()
    );

    this.subscriptions.push(
      this.submit$
        .pipe(
          tap(() => {
            this.http.baseUrl = this._baseUrl;
            this.spinner.on();
            this.http
              .fetch(this._buildRequest())
              .pipe(
                catchError(() =>
                  of(
                    this.toast.errorNotify(
                      this.translocoService.translate('MESSAGES.GENERIC_ERROR')
                    )
                  )
                ),
                tap((response: any) => {
                  this.router
                    .navigate([`${response.record.redirectUrl}`])
                    .then(() => window.location.reload());
                }),
                finalize(() => {
                  this._close$.next();
                  this.spinner.off();
                })
              )
              .subscribe();
          })
        )
        .subscribe()
    );

    this.subscriptions.push(
      this.spinner.spinnerState
        .pipe(
          tap((state) => {
            this.loading = state.show;
          })
        )
        .subscribe()
    );
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private _buildRequest(): Request {
    return {
      endpoint: ``,
      httpMethod: 'POST',
      body: { userKey: this.config?.userKey },
    };
  }
}
